import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../Layout';
import { listBaskets, getCompetitorData, parseCsvFile } from '../../services/trackingService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'bootstrap/dist/css/bootstrap.min.css';

/* ──────────────────────────────────────────── */
/* Detailed Table — port of detailed_table.py   */
/* Full sortable/filterable data table + export */
/* ──────────────────────────────────────────── */

const ALL_COLUMNS = [
    { key: '_index', label: '#' },
    { key: 'asin', label: 'ASIN' },
    { key: 'brand', label: 'Brand' },
    { key: 'product_title', label: 'Title' },
    { key: 'product_price', label: 'Price', fmt: v => v != null ? `$${Number(v).toFixed(2)}` : '—' },
    { key: 'product_original_price', label: 'Orig. Price', fmt: v => v != null ? `$${Number(v).toFixed(2)}` : '—' },
    { key: 'discount', label: 'Discount', fmt: v => v != null ? `${v}%` : '—' },
    { key: 'product_star_rating', label: 'Rating', fmt: v => v != null ? `⭐ ${Number(v).toFixed(2)}` : '—' },
    { key: 'product_num_ratings', label: 'Reviews', fmt: v => v != null ? Number(v).toLocaleString() : '—' },
    { key: 'rank_value', label: 'BSR', fmt: v => v != null ? `#${Number(v).toLocaleString()}` : '—' },
    { key: 'sub_category_name', label: 'Category' },
    { key: 'sales_volume', label: 'Sales Vol.' },
    { key: 'is_best_seller', label: 'Best Seller', fmt: v => v ? '✅' : '—' },
    { key: 'is_amazon_choice', label: "Amazon's Choice", fmt: v => v ? '✅' : '—' },
    { key: 'date', label: 'Date' },
    { key: 'week', label: 'Week' },
];

function DetailedTable() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [csvName, setCsvName] = useState(null);

    const [sortKey, setSortKey] = useState('date');
    const [sortDir, setSortDir] = useState('desc');
    const [filterText, setFilterText] = useState('');
    const [selectedBrand, setSelectedBrand] = useState('All');
    const [selectedDiscount, setSelectedDiscount] = useState('All');

    // Date Range State
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [visibleCols, setVisibleCols] = useState(ALL_COLUMNS.map(c => c.key));

    // Scroll refs
    const tableContainerRef = useRef(null);
    const topScrollRef = useRef(null);
    const [scrollWidth, setScrollWidth] = useState(0);

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try { const res = await listBaskets(accountId); setBaskets(res.data || []); } catch { }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        try {
            const res = await getCompetitorData(selectedBasket.id, accountId);
            const fetchedData = res.data || [];
            setData(fetchedData);
            setStartDate(null);
            setEndDate(null);
        }
        catch { } finally { setLoading(false); }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    // CSV upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        try {
            const res = await parseCsvFile(file);
            const fetchedData = res.data || [];
            setData(fetchedData);
            setCsvName(file.name);
            setSelectedBasket({ id: 'csv', name: file.name });
            setStartDate(null);
            setEndDate(null);
        } catch { }
        finally { setLoading(false); }
    };
    const handleClearCsv = () => { setCsvName(null); setSelectedBasket(null); setData([]); setStartDate(null); setEndDate(null); };

    // Unique brands for filter
    const uniqueBrands = useMemo(() => {
        return [...new Set(data.map(r => r.brand).filter(Boolean))].sort();
    }, [data]);

    // Filter + sort + Indexing
    const processed = useMemo(() => {
        let rows = [...data];

        // 0. Filter by Date Range
        if (startDate && endDate) {
            rows = rows.filter(r => {
                const d = new Date(r.date);
                return d >= startDate && d <= endDate;
            });
        }

        // 1. Filter by Brand
        if (selectedBrand !== 'All') {
            rows = rows.filter(r => r.brand === selectedBrand);
        }

        // 2. Filter by Discount
        if (selectedDiscount === 'Discounted') {
            rows = rows.filter(r => r.discount > 0);
        } else if (selectedDiscount === 'No Discount') {
            rows = rows.filter(r => !r.discount || r.discount === 0);
        }

        // 3. Filter by Text
        if (filterText) {
            const lower = filterText.toLowerCase();
            rows = rows.filter(r =>
                Object.values(r).some(v => String(v || '').toLowerCase().includes(lower))
            );
        }

        // 4. Sort
        rows.sort((a, b) => {
            const va = a[sortKey] ?? '';
            const vb = b[sortKey] ?? '';
            const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
            return sortDir === 'asc' ? cmp : -cmp;
        });

        // 5. Add Index (Row Counter)
        return rows.map((r, i) => ({ ...r, _index: i + 1 }));
    }, [data, filterText, sortKey, sortDir, selectedBrand, selectedDiscount, startDate, endDate]);

    // Sync scroll width
    useEffect(() => {
        if (tableContainerRef.current) {
            setScrollWidth(tableContainerRef.current.scrollWidth);
        }
    }, [processed, visibleCols]);

    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir('asc'); }
    };

    const toggleCol = (key) => {
        setVisibleCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    // CSV export
    const exportCSV = () => {
        const cols = ALL_COLUMNS.filter(c => visibleCols.includes(c.key));
        const header = cols.map(c => c.label).join(',');
        const rows = processed.map(r => cols.map(c => {
            const v = r[c.key];
            return v != null ? `"${String(v).replace(/"/g, '""')}"` : '';
        }).join(','));
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `competitor_data_${selectedBasket?.name || ''}.csv`;
        a.click(); URL.revokeObjectURL(url);
    };

    // Scroll sync handlers
    const handleScroll = (e) => {
        const target = e.target;
        if (target === topScrollRef.current && tableContainerRef.current) {
            tableContainerRef.current.scrollLeft = target.scrollLeft;
        } else if (target === tableContainerRef.current && topScrollRef.current) {
            topScrollRef.current.scrollLeft = target.scrollLeft;
        }
    };

    const activeCols = ALL_COLUMNS.filter(c => visibleCols.includes(c.key));

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Detailed Data Table</h2>

                {/* Date Range Filter */}
                <div className="card mb-3">
                    <div className="card-body py-2">
                        <label className="form-label small text-muted mb-1">Filter by date (range)</label>
                        <div className="d-flex gap-2">
                            <DatePicker
                                selected={startDate}
                                onChange={(dates) => {
                                    const [start, end] = dates;
                                    setStartDate(start);
                                    setEndDate(end);
                                }}
                                startDate={startDate}
                                endDate={endDate}
                                selectsRange
                                className="form-control form-control-sm"
                                placeholderText="Select date range"
                                isClearable
                            />
                        </div>
                    </div>
                </div>

                {/* Filters Row */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label small text-muted">Filter by Brand</label>
                        <select className="form-select" value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)}>
                            <option value="All">All</option>
                            {uniqueBrands.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small text-muted">Filter by Discount Status</label>
                        <select className="form-select" value={selectedDiscount} onChange={e => setSelectedDiscount(e.target.value)}>
                            <option value="All">All</option>
                            <option value="Discounted">Discounted</option>
                            <option value="No Discount">No Discount</option>
                        </select>
                    </div>
                </div>

                {/* Controls */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <select className="form-select" value={selectedBasket?.id || ''}
                            onChange={e => setSelectedBasket(baskets.find(b => b.id === parseInt(e.target.value)) || null)}>
                            <option value="">Select basket...</option>
                            {baskets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-4">
                        <input className="form-control" placeholder="Search text..." value={filterText} onChange={e => setFilterText(e.target.value)} />
                    </div>
                    <div className="col-md-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                                <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                            </label>
                            {csvName && (
                                <span className="badge bg-success d-flex align-items-center gap-1">
                                    {csvName}
                                    <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-md-4 d-flex gap-2">
                        <button className="btn btn-outline-success btn-sm" onClick={exportCSV} disabled={!processed.length}>
                            <i className="bi bi-download"></i> Export CSV
                        </button>
                        <div className="dropdown">
                            <button className="btn btn-outline-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown">Columns</button>
                            <ul className="dropdown-menu" style={{ maxHeight: 300, overflow: 'auto' }}>
                                {ALL_COLUMNS.map(c => (
                                    <li key={c.key}>
                                        <label className="dropdown-item">
                                            <input type="checkbox" checked={visibleCols.includes(c.key)} onChange={() => toggleCol(c.key)} className="me-2" />
                                            {c.label}
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {loading && <div className="text-center"><div className="spinner-border" /></div>}

                {/* Custom styles for sticky columns */}
                <style>
                    {`
                    .sticky-col { position: sticky !important; z-index: 10; background-color: #fff !important; }
                    .sticky-header { position: sticky !important; top: 0; z-index: 20; }
                    /* Fix for striped rows on sticky columns */
                    .table-striped > tbody > tr:nth-of-type(odd) > td.sticky-col { background-color: #f2f2f2 !important; }
                    .table-hover > tbody > tr:hover > td.sticky-col { background-color: #e2e6ea !important; }
                    
                    /* Specific widths for robustness */
                    .col-index { left: 0; min-width: 50px; max-width: 50px; border-right: 1px solid #dee2e6; text-align: center; font-weight: bold; }
                    .col-asin { left: 50px; min-width: 120px; max-width: 120px; border-right: 1px solid #dee2e6; }
                    .col-brand { left: 170px; min-width: 140px; max-width: 140px; border-right: 1px solid #dee2e6; }
                    
                    /* Header specific z-index */
                    th.sticky-col { z-index: 30 !important; background-color: #212529 !important; color: white; }
                    `}
                </style>

                {processed.length > 0 && (
                    <>
                        {/* Top Scrollbar */}
                        <div ref={topScrollRef} onScroll={handleScroll} style={{ overflowX: 'auto', marginBottom: '5px', width: '100%' }}>
                            <div style={{ width: scrollWidth, height: '1px' }}></div>
                        </div>

                        <div className="card shadow-sm">
                            <div className="card-body p-0">
                                <div className="table-responsive" ref={tableContainerRef} onScroll={handleScroll} style={{ maxHeight: '75vh', overflowX: 'auto' }}>
                                    <table className="table table-sm table-striped table-hover mb-0 align-middle">
                                        <thead className="table-dark sticky-header">
                                            <tr>
                                                {activeCols.map((c, idx) => {
                                                    // Determine if sticky
                                                    let stickyClass = '';
                                                    if (c.key === '_index') stickyClass = 'sticky-col col-index';
                                                    if (c.key === 'asin') stickyClass = 'sticky-col col-asin';
                                                    if (c.key === 'brand') stickyClass = 'sticky-col col-brand';

                                                    return (
                                                        <th key={c.key} className={stickyClass}
                                                            onClick={() => handleSort(c.key)}
                                                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                            {c.label} {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {processed.map((row, i) => (
                                                <tr key={i}>
                                                    {activeCols.map((c, idx) => {
                                                        let stickyClass = '';
                                                        if (c.key === '_index') stickyClass = 'sticky-col col-index text-muted';
                                                        if (c.key === 'asin') stickyClass = 'sticky-col col-asin fw-bold';
                                                        if (c.key === 'brand') stickyClass = 'sticky-col col-brand text-muted';

                                                        return (
                                                            <td key={c.key} className={stickyClass} style={{ whiteSpace: 'nowrap' }}>
                                                                {c.fmt ? c.fmt(row[c.key]) : (row[c.key] ?? '—')}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="card-footer text-muted d-flex justify-content-between">
                                <small>{processed.length} entries</small>
                                <small className="text-info">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Scroll horizontally to view more
                                </small>
                            </div>
                        </div>
                    </>
                )}

                {data.length === 0 && !loading && selectedBasket && (
                    <div className="text-center text-muted py-5">No data available. Fetch data from the Overview page first.</div>
                )}
            </div>
        </Layout>
    );
}

export default DetailedTable;
